/**
 * SeenByRow — who has read up to this message (2026-09-08, PR-3). Sits under
 * the bubble as its own row (the MessageTime meta row is nowrap and must stay
 * so). One small glyph per reader; the full list is the tooltip and the
 * accessible label. Pure presentational — the host computes the readers from
 * receipts (services/groupThreads/seenBy.ts).
 */
import React from 'react';
import { getUserUiLanguage } from '../../services/threadMetadata/uiLanguage';

export interface SeenByReader {
  id: string;
  label: string;
  glyph: string;
  /** Prest0n's receipt renders with the assistant mark. */
  assistant?: boolean;
}

export interface SeenByRowProps {
  readers: ReadonlyArray<SeenByReader>;
  /** Right-align under the viewer's own bubbles. */
  align?: 'left' | 'right';
}

export function seenByLabel(readers: ReadonlyArray<SeenByReader>, lang: 'en' | 'es'): string {
  const names = readers.map((r) => r.label).filter(Boolean);
  if (names.length === 0) return '';
  return lang === 'es' ? `visto por ${names.join(', ')}` : `seen by ${names.join(', ')}`;
}

export function SeenByRow({ readers, align = 'left' }: SeenByRowProps): React.ReactElement | null {
  if (!readers || readers.length === 0) return null;
  const lang = getUserUiLanguage() === 'es' ? 'es' : 'en';
  const label = seenByLabel(readers, lang);
  return (
    <div
      role="img"
      aria-label={label}
      title={label}
      data-seen-by={readers.map((r) => r.id).join(',')}
      style={{
        display: 'flex',
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        alignItems: 'center',
        gap: 3,
        marginTop: 4,
        lineHeight: 1,
        userSelect: 'none',
      }}
    >
      {readers.map((r) => (
        <span
          key={r.id}
          data-seen-by-reader={r.id}
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 14,
            height: 14,
            borderRadius: '50%',
            fontSize: 8,
            fontWeight: 700,
            letterSpacing: 0,
            color: r.assistant ? '#fff' : 'rgba(0,0,0,0.65)',
            background: r.assistant ? '#8a6d2f' : 'rgba(127,127,127,0.25)',
            border: '1px solid rgba(255,255,255,0.8)',
          }}
        >
          {r.glyph}
        </span>
      ))}
    </div>
  );
}

export default SeenByRow;
